// src/utils/jobPunchDays.js
// jobday-6b2f9e14
//
// THE ONLY PLACE a job's clocked hours may be split across calendar days.
//
// WHY THIS EXISTS. jobCosting.laborEntries holds ONE cumulative total per person per
// job, with no day dimension at all. The timesheet used to show that total on the day
// the job was SCHEDULED (job.startAt), which is not when the work happened — a job
// booked Tuesday and worked Wednesday put its hours on Tuesday, on a day the person
// may never have clocked in. This splits the same total by the day each punch STARTED.
//
// THIS IS NOT A DIFFERENT NUMBER. It is the same number's own inputs. The server's
// cumulative total is sumClockedHours (functions/index.js ~22663):
//
//     .filter(p => p && p.userId === uid && p.punchOut !== null && p.hours !== null)
//     .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0).toFixed(2)
//
// The filter below is a DELIBERATE BYTE-COPY of that line. Do not "improve" it — the
// copy is what makes three separate behaviours correct for free:
//
//   * A VOIDED punch (correctJobPunch op:'void') keeps its punchIn/punchOut but is set
//     to hours 0. It passes the closed filter and contributes exactly zero.
//   * A CAPPED punch (closeJobPunchesOnDone past the 13h believability limit) is also
//     hours 0 + needsReview. Same: passes, contributes zero.
//   * An OPEN punch has punchOut null, so it is EXCLUDED — it has no measured end, and
//     counting it would invent hours. (Web writes hours:null on punch-in, mobile writes
//     hours:0; the punchOut check catches both.)
//
// That is the whole reason the server encodes void and cap as `hours: 0` rather than as
// flags: no sum site has to learn the concepts exist. Copying the filter inherits all of
// it. Diverging from it is how the timesheet and the invoice start disagreeing.
//
// ROUNDING. Per-punch hours carry 4dp; the server rounds ONCE, at 2dp, over the summed
// float. So this returns UNROUNDED floats and the caller rounds at the end — per-day 2dp
// rounding would accumulate a different number than the invoice bills from.
//
// NOT MIRRORED INTO functions/. functions/ is a separate package and cannot import
// ../src (same reason PUNCH_BELIEVABLE_MS is inlined there). The server already has
// sumClockedHours; this is the web-side day split only.

// Timestamp | Date | string -> Date, or null. Same coercion as jobPunchInstant on the
// server and safeToDate in the views.
const punchInstant = (value) => {
    if (value === null || value === undefined) return null;
    const d = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Split one person's closed punches on one job into calendar-day buckets.
 *
 * @param {Array}    punches  job.timePunches
 * @param {string}   userId   the person whose laborEntries row is being split
 * @param {Function} dayKeyOf (Date) => string — the caller's day key, so this file never
 *                            decides what a "day" is (the view keys in browser-local time,
 *                            exactly as it does for shifts).
 * @returns {Map<string, {hours: number, anchor: Date}>} hours UNROUNDED; anchor is the
 *          earliest punchIn that day, so the caller can build a real day bucket from an
 *          instant rather than re-parsing the key string.
 */
export const closedPunchHoursByDay = (punches, userId, dayKeyOf) => {
    const byDay = new Map();
    (Array.isArray(punches) ? punches : []).forEach(p => {
        // BYTE-COPY of sumClockedHours' filter — see the header. `!== null` (not a
        // truthiness check) is deliberate: it matches the server character for character.
        if (!(p && p.userId === userId && p.punchOut !== null && p.hours !== null)) return;
        const inAt = punchInstant(p.punchIn);
        if (!inAt) return; // unparseable punchIn — no day to attribute it to
        const key = dayKeyOf(inAt);
        const prev = byDay.get(key);
        if (prev) {
            prev.hours += (parseFloat(p.hours) || 0);
            if (inAt < prev.anchor) prev.anchor = inAt;
        } else {
            byDay.set(key, { hours: (parseFloat(p.hours) || 0), anchor: inAt });
        }
    });
    return byDay;
};

/**
 * Sum the day buckets back, rounded EXACTLY as the server rounds its cumulative total
 * (2dp, once, over the summed float). Used to reconcile against laborEntries.hours.
 */
export const sumDayBuckets = (byDay) =>
    parseFloat(
        Array.from(byDay.values()).reduce((sum, b) => sum + b.hours, 0).toFixed(2)
    );
